<script setup lang="ts">
import type {NavigationMenuItem} from "@nuxt/ui";
import type {DropdownMenuItem} from "@nuxt/ui/components/DropdownMenu.vue";
import {userHasRole} from "~/utils/user";
import DevToolbar from "~/components/DevToolbar.vue";

const config = useRuntimeConfig();
const {isAuthenticated, logout} = useSanctumAuth();
const appUser = useAppUser();
const {currentMode, toggleMode, hasBothRoles} = useInterfaceMode();
const { show: showConsent } = useConsent();
const route = useRoute();
const isDrawerOpen = ref(false);
const isMobileHeaderOpen = ref(false);
const emailVerified = computed(() => route.query.verified === "1");
const isReader = computed(() => appUser.value && userHasRole(appUser.value, "reader"));
const isAuthor = computed(() => appUser.value && userHasRole(appUser.value, "author"));
const isAdmin = computed(() => appUser.value && (userHasRole(appUser.value, "admin") || userHasRole(appUser.value, "superAdmin")));

const mainNavItems = computed<NavigationMenuItem[]>(() => {
  let items = [];

  if (isAuthenticated.value) {
    items.push(
        {
          label: "Découvrir",
          to: "/decouvrir",
        },
    );
  }

  if (!isAuthenticated.value) {
    items.push({
      label: "Lire",
      to: "/lire",
    });
  }

  if (!isAuthenticated.value || (isAuthenticated.value && !isAuthor.value)) {
    items.push({
      label: "Écrire",
      to: "/ecrire",
    });
  }

  items = items.concat([
    {
      label: "À propos",
      to: "/a-propos",
    },
    {
      label: "Aide",
      to: "/aide",
    },
  ]);

  return items;
});

const userAvatarSrc = computed(() => {
  return currentMode.value === "author" ? appUser.value?.profiles?.author?.picture : (currentMode.value === "reader" ? appUser.value?.profiles?.reader?.picture : undefined);
});
const authorMenuItems = computed<DropdownMenuItem[][]>(() => {
  const items: DropdownMenuItem[][] = [
    [
      {
        label: "Tableau de bord",
        icon: "i-heroicons-home",
        to: "/dashboard",
      },
    ],
    [
      {
        label: "Profil",
        icon: "i-heroicons-user",
        to: "/dashboard/profile",
      },
    ],
  ];

  if (hasBothRoles.value) {
    items.push([
      {
        label: "Passer en mode Lecteur",
        icon: "i-heroicons-arrow-path-rounded-square",
        onSelect: () => toggleMode(),
      },
    ]);
  }

  items.push([
    {
      label: "Logout",
      icon: "i-heroicons-arrow-right-on-rectangle-solid",
      onSelect: () => logout(),
    },
  ]);

  return items;
});
const readerMenuItems = computed<DropdownMenuItem[][]>(() => {
  const items: DropdownMenuItem[][] = [
    [
      {
        label: "Dashboard",
        icon: "i-heroicons-home",
        to: "/dashboard",
      },
    ],
    [
      {
        label: "Mon profil",
        icon: "i-heroicons-user",
        to: "/dashboard/profile",
      },
    ],
  ];

  if (hasBothRoles.value) {
    items.push([
      {
        label: "Passer en mode Auteur",
        icon: "i-heroicons-arrow-path-rounded-square",
        onSelect: () => toggleMode(),
      },
    ]);
  }

  items.push([
    {
      label: "Logout",
      icon: "i-heroicons-arrow-right-on-rectangle-solid",
      onSelect: () => logout(),
    },
  ]);

  return items;
});

const userMenuItems = computed<DropdownMenuItem[][]>(() => {
  let items: DropdownMenuItem[][] | { label: string; icon: string; to: string; }[][] = [];

  if (currentMode.value === "author") {
    items = authorMenuItems.value;
  }

  if (currentMode.value === "reader") {
    items = readerMenuItems.value;
  }

  if (isAdmin.value) {
    items.push([{
      label: "Admin",
      icon: "i-heroicons-cog",
      to: config.public.apiBase + "/dashboard",
      target: "_blank",
    }]);
  }

  return items;
});

const userReaderResponsiveMenuItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [
    [
      {
        label: "Dashboard",
        to: "/dashboard",
      },
      {
        label: "Mon profil",
        to: "/dashboard/profile",
      },
    ],
  ];

  return items;
});

const userAuthorWriteResponsiveMenuItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [
    [
      {
        label: "Dashboard",
        icon: "i-heroicons-home",
        to: "/dashboard",
      },
      {
        label: "Mes histoires",
        icon: "i-custom-book-pile",
        to: "/dashboard/projects",
      },
      {
        label: "Créer une histoire",
        icon: "i-heroicons-plus",
        to: "/dashboard/projects/add",
      },
    ],
  ];

  return items;
});
const userAuthorCommunityResponsiveMenuItems = computed<NavigationMenuItem[]>(() => {
  return [
    [
      {
        label: "Commentaires reçus",
        icon: "i-custom-comment",
        to: "/dashboard/comments",
      },
      {
        label: "Voter pour une feature",
        icon: "i-custom-lightbulb",
        to: "/dashboard/features",
      },
    ],
  ];
});
const userAuthorAccountResponsiveMenuItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [
    [
      {
        label: "Profil",
        icon: "i-heroicons-user",
        to: "/dashboard/profile",
      },
      {
        label: "Revenus et scribs",
        icon: "i-custom-coin",
        to: "/dashboard/scribs",
      },
      {
        label: "Mes notifications",
        icon: "i-custom-bell",
        to: "/dashboard/notifications",
      },
      {
        label: "Mon abonnement",
        icon: "i-heroicons-credit-card",
        to: "/dashboard/subscription",
      },
    ],
  ];

  if (hasBothRoles.value) {

    if (currentMode.value === "reader") {
      items.push([
        {
          label: "Passer en mode Auteur",
          icon: "i-heroicons-arrow-path-rounded-square",
          onSelect: () => toggleMode(),
        },
      ]);
    } else if (currentMode.value === "author") {
      items.push([
        {
          label: "Passer en mode Lecteur",
          icon: "i-heroicons-arrow-path-rounded-square",
          onSelect: () => toggleMode(),
        },
      ]);
    }

  }

  return items;
});

watch(() => route.fullPath, () => {
  isDrawerOpen.value = false;
  isMobileHeaderOpen.value = false;
});

const uiNavMenu = {
  link: "px-0 before:content-none data-active:font-bold",
};
</script>

<template>
  <UHeader v-model:open="isMobileHeaderOpen" id="appHeader" class="border-b-2 border-primary" :ui="{content:'rounded-none'}">
    <template #title>
      <img src="/Wreadia-logo.svg" alt="WREADIA" class="w-[139px]">
    </template>

    <template #right>
      <UNavigationMenu :items="mainNavItems" class="hidden md:flex mr-8" :ui="{link:'text-base text-gray-400'}"/>
      <ULink to="/dashboard?tab=scribs" v-if="isReader && appUser && currentMode === 'reader'">
        <UBadge icon="i-custom-coin" variant="outline" size="md" class="rounded-xs  !bg-white border-gray-200 p-2 mr-8">
          {{ appUser.scribs_balance }}
        </UBadge>
      </ULink>
      <UDropdownMenu v-if="isAuthenticated && appUser" :items="userMenuItems" class="hidden md:flex">
        <UAvatar
            :src="userAvatarSrc"
            :alt="appUser.name"
            size="xl"
        />
      </UDropdownMenu>
      <UButton v-else color="secondary" variant="solid" to="/login" class="hidden md:flex" size="sm">Connexion</UButton>
    </template>

    <template #body>
      <div>
        <UNavigationMenu :items="mainNavItems" orientation="vertical"/>
        <USeparator class="mt-2 mb-4"></USeparator>
      </div>
      <div v-if="!isAuthenticated">
        <UButton icon="i-heroicons-arrow-right-end-on-rectangle-solid" color="secondary" variant="solid" to="/login" size="sm">Connexion</UButton>
      </div>
      <div v-else>
        <template v-if="isAuthor || isReader">
          <template v-if="currentMode === 'author'">
            <UNavigationMenu orientation="vertical" :items="userAuthorWriteResponsiveMenuItems"></UNavigationMenu>
            <USeparator class="mt-2 mb-4"></USeparator>
            <UNavigationMenu orientation="vertical" :items="userAuthorCommunityResponsiveMenuItems"></UNavigationMenu>
            <USeparator class="mt-2 mb-4"></USeparator>
            <UNavigationMenu orientation="vertical" :items="userAuthorAccountResponsiveMenuItems"></UNavigationMenu>
          </template>
          <template v-else>
            <UNavigationMenu orientation="vertical" :items="userReaderResponsiveMenuItems"></UNavigationMenu>
          </template>
          <USeparator class="mt-2 mb-4"></USeparator>
        </template>
        <UButton icon="i-heroicons-arrow-right-on-rectangle-solid" size="sm" variant="outline" @click="() => logout()">Déconnexion</UButton>
      </div>
    </template>
  </UHeader>

  <UMain id="appMain" class="min-h-[calc(100vh-var(--ui-header-height)-var(--ui-footer-height))]">
    <UAlert v-if="emailVerified" color="success" variant="solid" title="Votre adresse email a bien été vérifiée."></UAlert>

    <slot></slot>
  </UMain>

  <div id="appFooter" class="border-primary border-t-2 py-8">
    <UContainer>
      <div class="flex flex-col lg:flex-row justify-between gap-y-4">
        <div>© WREADIA 2026</div>
        <nav>
          <ul class="flex flex-wrap gap-x-4">
            <li>
              <NuxtLink to="/a-propos">À propos</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/charte">Charte</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/faq">FAQ</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/mentions-legales">Mentions légales</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/confidentialite">Confidentialité</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/cgv">Conditions générales de vente</NuxtLink>
            </li>
            <li>
              <button @click="showConsent" class="cursor-pointer hover:underline">Cookies</button>
            </li>
          </ul>
        </nav>
        <ul class="flex flex-wrap gap-4">
          <li><a href="https://www.instagram.com/_wreadia_/" target="_blank">
            <UIcon name="i-simple-icons-instagram" class="size-6"></UIcon>
          </a></li>
          <li><a href="https://www.linkedin.com/company/wreadia/" target="_blank">
            <UIcon name="i-simple-icons-linkedin" class="size-6"></UIcon>
          </a></li>
        </ul>
      </div>
    </UContainer>
  </div>

  <DevToolbar></DevToolbar>

  <div v-if="isAuthor || isReader" class="lg:hidden fixed bottom-0 w-full p-4 border-t bg-white">
    <nav v-if="isAuthor">
      <ul class="flex justify-between w-full">
        <li>
          <UDrawer v-model:open="isDrawerOpen" direction="left" inset :handle="false">
            <div class="flex flex-col items-center">
              <UIcon name="i-heroicons-list-bullet" class="size-6"></UIcon>
              <div class="text-xs">Espace auteur</div>
            </div>
            <template #body>
              <div class="mb-4">
                <p class="font-semibold mb-1">Écrire</p>
                <UNavigationMenu orientation="vertical"
                                 :items="userAuthorWriteResponsiveMenuItems"
                                 :ui="uiNavMenu"
                ></UNavigationMenu>
              </div>
              <div class="mb-4">
                <p class="font-semibold mb-1">Communauté</p>
                <UNavigationMenu orientation="vertical"
                                 :items="userAuthorCommunityResponsiveMenuItems"
                                 :ui="uiNavMenu"
                ></UNavigationMenu>
              </div>
              <div class="mb-4">
                <p class="font-semibold mb-1">Compte</p>
                <UNavigationMenu orientation="vertical"
                                 :items="userAuthorAccountResponsiveMenuItems"
                                 :ui="uiNavMenu"
                ></UNavigationMenu>
              </div>
            </template>
          </UDrawer>

        </li>
        <li>
          <ULink to="/dashboard" class="flex flex-col items-center">
            <UIcon name="i-heroicons-home" class="size-6"></UIcon>
            <div class="text-xs">Tableau de bord</div>
          </ULink>
        </li>
        <li>
          <ULink to="/dashboard/projects/add" class="flex flex-col items-center">
            <UIcon name="i-heroicons-plus" class="size-6"></UIcon>
            <div class="text-xs">Créer</div>
          </ULink>
        </li>
        <li>
          <ULink to="/dashboard/projects" class="flex flex-col items-center">
            <UIcon name="i-custom-book-pile" class="size-6"></UIcon>
            <div class="text-xs">Mes histoires</div>
          </ULink>
        </li>
        <li>
          <ULink to="/dashboard/profile" class="flex flex-col items-center">
            <UIcon name="i-heroicons-user" class="size-6"></UIcon>
            <div class="text-xs">Mon profil</div>
          </ULink>
        </li>
      </ul>
    </nav>
    <nav v-else-if="isReader">
      <ul class="flex justify-around w-full">
        <li>
          <ULink to="/dashboard" class="flex flex-col items-center">
            <UIcon name="i-heroicons-home" class="size-6"></UIcon>
            <div class="text-xs">Dashboard</div>
          </ULink>
        </li>
        <li>
          <ULink to="/dashboard/profile" class="flex flex-col items-center">
            <UIcon name="i-heroicons-user" class="size-6"></UIcon>
            <div class="text-xs">Profil</div>
          </ULink>
        </li>
      </ul>
    </nav>
  </div>
</template>
